import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/web/frontend/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/web/frontend/markdoc/tags.js'
import * as nodes from '/vercel/path0/web/frontend/markdoc/nodes.js'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer({"allowComments":true});

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: \"Automatically catch spend spikes with Anomaly Detection\"\ndescription: \"Built-in anomaly detection is now available in monitors, automatically accounting for seasonal spending patterns. This new feature ensures you’re alerted only to genuine anomalies, with customizable sensitivity to minimize false positives.\"\ndate: \"2025-03-20T12:00:00.000Z\"\nauthorNames: [morgan, fernandobrito]\nimage:  c952905e-c28a-47da-4246-75d9bd0c6600\n---\n\nOur existing monitors allow users to set static thresholds to alert them when resource spend exceeds a predefined amount. However, determining the right threshold can be challenging—especially when spending follows seasonal patterns. For example, if a workload is expected to spike every Monday but not on other days, a threshold set to accommodate that pattern might miss anomalies on other days.\n\nTo solve this, we’ve integrated built-in anomaly detection into monitors. This new feature uses an algorithm that automatically accounts for seasonality in your spend data, ensuring you’re alerted only to genuine spending anomalies while filtering out regular, predictable fluctuations.\n\n{% blog-post-image imageName=\"7b4214e3-4dfe-4dd5-a95c-6cb9329aa900\" alt=\"SELECT Automatically catch spend spikes with Anomaly Detection\" imgClassName=\"rounded-md ring-1 ring-white/20\" /%}\n\nAdditionally, you can fine-tune the algorithm’s sensitivity, allowing you to strike the perfect balance between rapid detection and minimizing false positives according to your own needs.\n\n{% blog-post-image imageName=\"f933ac96-d66b-44ca-09b8-cea5597eb000\" alt=\"SELECT Automatically catch spend spikes with Anomaly Detection\" imgClassName=\"rounded-md ring-1 ring-white/20\" /%}\n\n**Other Things We shipped**\n\n- 🚀 To clarify user management actions, we've introduced a SELECT Organization level access role. This controls destructive actions like removing a user from your SELECT Organization. Any pre-existing Snowflake Organisation roles have been migrated into SELECT Organisation roles to ensure no loss in privileges.\n";
const filepath = "/changelog/2025-03-20-anomaly-spike-spends.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens, {slots: false});

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

async function getMarkdocData(context = {}) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  // Removes undefined
  return JSON.parse(
    JSON.stringify({
      content,
      frontmatter,
      file: {
        path: filepath,
      },
    })
  );
}

export async function getStaticProps(context) {
  return {
    props: {
      markdoc: await getMarkdocData(context),
    },
  };
}

export default function MarkdocComponent(props) {
  const markdoc = props.markdoc;
  // Only execute HMR code in development
  return renderers.react(markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
